import { useI18n } from "vue-i18n";
import { useProfileStore } from "@/modules/settings/public";
import { formatDate as formatDateUtil } from "@/shared/utils/date-formatting";
/**
 * Shared helpers for the Contact / Organisation list views: locale-aware date
 * formatting, enum-code translation with a raw-value fallback, a "hide column"
 * action, and an opt-in column builder. Extracted so a fix to one (e.g. the date
 * format) can't silently diverge between the two list views.
 */
export function useListColumns(options) {
    const { t, locale } = useI18n();
    const profileStore = useProfileStore();
    // Delegates to the shared util so the new date columns honor the user's
    // profile.dateFormat (R30), matching ContactPreviewDrawer / ContactDedupPanel
    // instead of rendering in the locale default only.
    const formatDate = (val) => {
        if (!val)
            return "–";
        const iso = val instanceof Date ? val.toISOString() : val;
        return formatDateUtil(iso, locale.value, profileStore.profile?.dateFormat);
    };
    // Translate an enum code, falling back to the raw value when the key is missing
    // (vue-i18n echoes the key back). Mirrors the detail views' label pattern.
    const tOrRaw = (key, raw) => {
        const translated = t(key);
        return translated === key ? raw : translated;
    };
    // Locale-aware display name for an ISO 3166-1 alpha-2 region code (e.g. "FR" ->
    // "France" / "Frankrijk"). Avoids a hand-maintained code->label i18n map; falls
    // back to the raw code if the runtime can't resolve it.
    const formatRegion = (code) => {
        if (!code)
            return "–";
        try {
            return new Intl.DisplayNames([locale.value], { type: "region" }).of(code) ?? code;
        }
        catch {
            return code;
        }
    };
    // Locale-aware display name for a BCP 47 language tag (e.g. "fr-FR" -> "French"
    // / "français"). Same rationale and fallback as formatRegion.
    const formatLanguage = (tag) => {
        if (!tag)
            return "–";
        try {
            return new Intl.DisplayNames([locale.value], { type: "language" }).of(tag) ?? tag;
        }
        catch {
            return tag;
        }
    };
    const hideColumn = (key) => {
        const updated = options.columnSettings.settingsItems.value.map((c) => ({
            ...c,
            visible: c.key === key ? false : c.visible,
        }));
        options.columnSettings.handleSave(updated);
    };
    // Builds an opt-in column whose only header action is "hide" (no server-side sort —
    // sortable columns require a matching key in mapSortField + the upstream list query).
    function optionalColumn(id, cell) {
        return {
            id,
            header: t(`${options.headerNamespace}.${id}`),
            ...(options.minWidth != null ? { minWidth: options.minWidth } : {}),
            cell,
            draggable: true,
            headerMenuItems: [{ label: t("table.hideColumn"), onClick: () => hideColumn(id) }],
        };
    }
    return { formatDate, tOrRaw, formatRegion, formatLanguage, hideColumn, optionalColumn };
}
